import {useAuth} from '@apps-common/auth-react';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import LoginIcon from '@mui/icons-material/LoginOutlined';
import Logout from '@mui/icons-material/Logout';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import {arrayOf, bool, node, string} from 'prop-types';
import React, {useCallback, useEffect} from 'react';
import logoUrl from '../assets/planet-publications-logo.png';
import Sparkles from '../assets/sparkles.svg';
import Footer from './Footer.jsx';
import Link from './Link.jsx';
import '../assets/css/style.css';

const defaultDescription =
  "Explore the publications database to find scientific papers, conference proceedings, and other resources leveraging Planet's data and tools.";

/**
 * @param {import('@apps-common/auth/client.js').User} user The user.
 */
function getUserInitials(user) {
  const claims = user.claims;
  if (!claims.given_name || !claims.family_name) {
    return '';
  }
  const initials = claims.given_name.charAt(0) + claims.family_name.charAt(0);
  return initials.toUpperCase();
}

export const newLink = (
  <MenuItem component={Link} to="/new" key="new">
    <ListItemIcon>
      <AddIcon fontSize="small" />
    </ListItemIcon>
    New publication
  </MenuItem>
);

export const editLink = (
  <MenuItem component={Link} to="./edit" key="edit">
    <ListItemIcon>
      <EditIcon fontSize="small" />
    </ListItemIcon>
    Edit publication
  </MenuItem>
);

function Page({
  title = 'Planet Publications',
  description = defaultDescription,
  children,
  scroll = true,
  links = [],
  withFooter = true,
  askAi,
}) {
  const auth = useAuth();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://widget.kapa.ai/kapa-widget.bundle.js';
    script.async = true;
    script.setAttribute(
      'data-modal-override-open-id-ask-ai',
      'kapa-ask-ai-widget',
    );
    script.setAttribute('data-modal-override-open-id', 'kapa-ask-ai-widget');
    script.setAttribute(
      'data-website-id',
      'dd5b55a7-cd05-4192-8fba-464d03bd6d81',
    );
    script.setAttribute('data-button-hide', 'true');
    script.setAttribute(
      'data-project-logo',
      'https://everpath-course-content.s3-accelerate.amazonaws.com/instructor%2F5jjhh8gka5wr1gk4qk20yykgu%2Fpublic%2F1745393676%2FPlanet-Logo-Primarymark-White.1745393675587.png',
    );
    script.setAttribute(
      'data-modal-disclaimer',
      'This is an AI assistant which has access to the content in the Planet Publications Database, Planet documentation, API specifications, GitHub repositories, Planet Community, Planet University, and Help Center. Please validate all answers and check any code before running it, as there can be errors. Do not enter personal information or API keys. \n\nPlease be advised, the Publications Database does not contain full paper text, only abstracts and metadata. If you are interested in learning more about a paper’s methodology or specifics, please follow the links included in each listing',
    );
    script.setAttribute('data-modal-title', 'Planet AI Assistant');
    script.setAttribute('data-modal-z-index', '999');
    script.setAttribute('data-project-name', 'Planet');
    script.setAttribute('data-project-color', '#098D9E');
    script.setAttribute('data-modal-header-bg-color', '#098D9E');
    script.setAttribute('data-modal-title-color', '#ffffff');
    script.setAttribute(
      'data-modal-example-questions-title',
      'Example questions',
    );

    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const openKapaModal = () => {
    if (window.Kapa) {
      window.Kapa('open');
      setTimeout(() => {
        document
          .getElementById('kapa-widget-container')
          .shadowRoot.querySelector('.mantine-Modal-close').style.background =
          'white';
      }, 100);
    }
  };

  useEffect(() => {
    if (!location.hostname.endsWith('.planet.com')) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
    script.async = true;
    script.setAttribute(
      'data-domain-script',
      '5bb5cbfd-f037-4c90-9b26-43479b3d24ca',
    );

    document.head.appendChild(script);
    window.OptanonWrapper = function () {};

    return () => {
      document.head.removeChild(script);
      delete window.OptanonWrapper;
    };
  }, []);

  const signOut = useCallback(() => {
    auth.signOut();
  }, [auth]);

  const signIn = useCallback(() => {
    auth.signIn();
  }, [auth]);

  const overflow = scroll ? 'visible' : 'hidden';

  return (
    <>
      <title>{title}</title>
      <meta name="og:title" content={title} />
      <meta name="og:image" content={logoUrl} />
      <meta name="og:type" content="website" />
      <meta name="og:url" content={location.href} />
      <meta name="og:description" content={description} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'absolute',
          minHeight: '100%',
          width: '100%',
          top: 0,
        }}
      >
        <AppBar color="inherit" sx={{backgroundColor: 'black'}} elevation={1}>
          <Toolbar sx={{px: [0.5, 2]}}>
            <Link
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                mr: [0.5, 1],
                height: [34, 40],
              }}
            >
              <img
                src={logoUrl}
                alt="Planet Publication"
                style={{height: '100%'}}
              />
            </Link>
            <Box
              sx={{marginLeft: 'auto', display: 'flex', alignItems: 'center'}}
            >
              {askAi && (
                <button className="kapa-button" onClick={openKapaModal}>
                  <img
                    src={Sparkles}
                    alt="sparkles icon"
                    className="kapa-button-icon"
                  />
                  Ask AI
                </button>
              )}
              <Box sx={{display: ['flex'], alignItems: 'center'}}>
                {!auth.user && (
                  <IconButton
                    sx={{ml: [0, 1]}}
                    color="primary"
                    onClick={signIn}
                    disabled={auth.pending}
                  >
                    <Tooltip title="sign in">
                      <LoginIcon />
                    </Tooltip>
                  </IconButton>
                )}

                {auth.user && (
                  <IconButton
                    onClick={handleClick}
                    size="small"
                    sx={{ml: [0, 1]}}
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                  >
                    <Avatar
                      sx={{
                        backgroundColor: 'primary.main',
                        width: 32,
                        height: 32,
                        fontSize: 'small',
                      }}
                    >
                      {getUserInitials(auth.user)}
                    </Avatar>
                  </IconButton>
                )}
                <Menu
                  anchorEl={anchorEl}
                  id="account-menu"
                  open={open}
                  onClose={handleClose}
                  onClick={handleClose}
                  slotProps={{
                    paper: {
                      elevation: 0,
                      sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                          width: 32,
                          height: 32,
                          ml: -0.5,
                          mr: 1,
                        },
                        '&::before': {
                          content: '""',
                          display: 'block',
                          position: 'absolute',
                          top: 0,
                          right: 14,
                          width: 10,
                          height: 10,
                          bgcolor: 'background.paper',
                          transform: 'translateY(-50%) rotate(45deg)',
                          zIndex: 0,
                        },
                      },
                    },
                  }}
                  transformOrigin={{horizontal: 'right', vertical: 'top'}}
                  anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                >
                  {links}
                  <Divider />
                  <MenuItem onClick={signOut}>
                    <ListItemIcon>
                      <Logout fontSize="small" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
        <Toolbar />
        <Box
          sx={{overflow, display: 'flex', flexDirection: 'column', flexGrow: 1}}
        >
          <Container
            maxWidth="lg"
            sx={{
              overflow,
              display: 'flex',
              flexDirection: 'column',
              paddingTop: 3,
              flexGrow: 1,
            }}
          >
            {children}
          </Container>
        </Box>
        {withFooter && (
          <Box px={3}>
            <Footer />
          </Box>
        )}
      </Box>
    </>
  );
}

Page.propTypes = {
  title: string,
  description: string,
  children: node,
  links: arrayOf(node),
  scroll: bool,
  withFooter: bool,
  askAi: bool,
};

export default Page;
